import type { Item, ItemStatus, Part, PartType, Site } from '../../domain/inventory';

/**
 * What a site has against what its systems need (tidaltech/koi#8261), in
 * place of the sheet's hand-written "mangler 1 kamera, 1 UC for mye".
 *
 * Every system needs a camera, a winch, a unit controller (Gen 1) or
 * powerbox (Gen 2), an umbilical and a mounting kit; a site with systems
 * needs a site controller. The number of systems is the site's ordered
 * systems, or its cameras when nothing is ordered. A site with no systems
 * should have nothing left, so everything assigned to it is extra.
 *
 * The site's own systems are counted by the scarcest of camera, winch and
 * unit controller/powerbox (Sivert, #8261): a spare camera or winch waiting
 * to replace a faulty one doesn't make another system.
 */
export type SiteComponent =
  | 'cameras'
  | 'winches'
  | 'controlUnits'
  | 'umbilicals'
  | 'mountingKits'
  | 'siteControllers'
  | 'waterQualitySensors'
  | 'other';

export interface ComponentGap {
  component: SiteComponent;
  /** Negative when parts are missing, positive when there are extra. */
  difference: number;
}

export interface SiteCompleteness {
  /** Systems the site should have: ordered, or its cameras when not set. */
  expected: number;
  /** Systems it has: the fewest of cameras, winches and control units. */
  assembled: number;
  /**
   * Of those, systems running: the fewest of ACTIVE cameras, winches and
   * control units, at most `assembled`. A spare left ACTIVE doesn't count.
   */
  active: number;
  gaps: ComponentGap[];
}

const componentByPartType: Partial<Record<PartType, SiteComponent>> = {
  CAMERA_SYSTEM: 'cameras',
  WINCH_SYSTEM: 'winches',
  UNIT_CONTROLLER: 'controlUnits',
  POWERBOX: 'controlUnits',
  UMBILICAL: 'umbilicals',
  MOUNTING_KIT: 'mountingKits',
  SITE_CONTROLLER: 'siteControllers',
  WATER_QUALITY_SENSOR: 'waterQualitySensors',
};

const perSystem: SiteComponent[] = ['winches', 'controlUnits', 'umbilicals', 'mountingKits'];

// Still assigned to the site but back with us or gone: not at the site. A
// replaced unit may still be there, but its replacement is what counts.
const notAtSite = new Set<ItemStatus>([
  'REPLACED',
  'RETURN_SHIPPED_TO_WAREHOUSE',
  'RETURN_DELIVERED_TO_WAREHOUSE',
  'IN_WAREHOUSE_STORAGE',
  'CLEANING',
  'EOL_TESTING',
  'SCRAP',
]);

const labels: Record<SiteComponent, [string, string]> = {
  cameras: ['camera', 'cameras'],
  winches: ['winch', 'winches'],
  controlUnits: ['unit controller/powerbox', 'unit controllers/powerboxes'],
  umbilicals: ['umbilical', 'umbilicals'],
  mountingKits: ['mounting kit', 'mounting kits'],
  siteControllers: ['site controller', 'site controllers'],
  waterQualitySensors: ['water quality sensor', 'water quality sensors'],
  other: ['other part', 'other parts'],
};

export function siteCompleteness(
  site: Site,
  assignedItems: readonly Item[],
  partsById: ReadonlyMap<string, Part>,
): SiteCompleteness {
  const counts = new Map<SiteComponent, number>();
  const activeCounts = new Map<SiteComponent, number>();
  for (const item of assignedItems) {
    if (item.siteId !== site.id || (item.status && notAtSite.has(item.status))) continue;
    const type = partsById.get(item.partId)?.type;
    if (!type) continue;
    const component = componentByPartType[type] ?? 'other';
    counts.set(component, (counts.get(component) ?? 0) + 1);
    if (item.status === 'ACTIVE') {
      activeCounts.set(component, (activeCounts.get(component) ?? 0) + 1);
    }
  }
  const count = (component: SiteComponent) => counts.get(component) ?? 0;
  const ordered = site.orderedSystems;
  const systems = ordered ?? count('cameras');
  const assembled = Math.min(count('cameras'), count('winches'), count('controlUnits'));
  const activeCount = (component: SiteComponent) => activeCounts.get(component) ?? 0;
  const active = Math.min(
    assembled,
    activeCount('cameras'),
    activeCount('winches'),
    activeCount('controlUnits'),
  );

  const gaps: ComponentGap[] = [];
  const add = (component: SiteComponent, difference: number) => {
    if (difference !== 0) gaps.push({ component, difference });
  };
  if (systems === 0) {
    for (const [component, n] of counts) add(component, n);
  } else {
    if (ordered !== undefined) add('cameras', count('cameras') - ordered);
    for (const component of perSystem) add(component, count(component) - systems);
    if (count('siteControllers') === 0) add('siteControllers', -1);
  }
  const order = Object.keys(labels);
  gaps.sort((a, b) => order.indexOf(a.component) - order.indexOf(b.component));
  return { expected: systems, assembled, active, gaps };
}

function describe(gap: ComponentGap): string {
  const n = Math.abs(gap.difference);
  return `${n} ${labels[gap.component][n === 1 ? 0 : 1]}`;
}

/** "Missing 3 umbilicals, 5 mounting kits" and "Extra 1 site controller", or [] when complete. */
export function describeCompleteness({ gaps }: SiteCompleteness): string[] {
  const missing = gaps.filter((gap) => gap.difference < 0).map(describe);
  const extra = gaps.filter((gap) => gap.difference > 0).map(describe);
  return [
    ...(missing.length ? [`Missing ${missing.join(', ')}`] : []),
    ...(extra.length ? [`Extra ${extra.join(', ')}`] : []),
  ];
}

/**
 * Nothing ordered, nothing on site and nothing to bring back: e.g. an old
 * "B" site from the dual biomass/feeding cameras. Hidden from the status
 * table unless asked for.
 */
export function isEmptySite(
  site: Pick<Site, 'orderedSystems'>,
  completeness: SiteCompleteness,
): boolean {
  return !site.orderedSystems && completeness.assembled === 0 && completeness.gaps.length === 0;
}
